import { createContext, useContext, useEffect, useState } from 'react';
import { useLanguage } from '../localization';

export interface CommunitySocial {
  label: string;
  href: string;
  icon: string;
}

/** How prominently a team member is shown (controls card size). */
export type TeamTier = 'core' | 'admin' | 'moderator';

/** Which game sites a team member appears on. */
export type TeamScope = 'platform' | 'kf1' | 'minecraft';

export interface CommunityLeader {
  name: string;
  role: string;
  /** How prominently they're shown: core (big), admin (medium), moderator (compact). */
  tier: TeamTier;
  /** "platform" for network-wide; a game id for game-specific staff. */
  scope: TeamScope;
  /** Short role pill shown on the card (e.g. "Owner", "Core Team", "Admin", "Mod"). */
  badge?: string;
  /** Avatar image URL; falls back to `initials` on error / when absent. */
  avatar?: string;
  /** 2-letter fallback rendered when no avatar is available. */
  initials?: string;
  /** Longer blurb shown on richer (core/admin) cards. */
  description?: string;
  /** Skill chips shown on core cards. */
  specialties?: string[];
}

export interface CommunityConductItem {
  title: string;
  summary: string;
  /** Detailed rules under this category (rendered in full on the hub /conduct page). */
  rules?: string[];
}

export interface CommunityData {
  social: CommunitySocial[];
  discordInvite: string;
  leadership: CommunityLeader[];
  conduct: CommunityConductItem[];
}

/**
 * Inlined seed/fallback. Mirrors the shared `/api/community` payload so the
 * site renders correct identity (Discord link especially) even if the API is
 * briefly unreachable. This is the ONLY place a hardcoded discord.gg literal
 * should live in this app.
 */
const FALLBACK_COMMUNITY: CommunityData = {
  social: [
    { label: 'Discord', href: 'https://discord.gg/vGX8GSrej6', icon: 'discord' },
    { label: 'Steam', href: 'https://steamcommunity.com/groups/uliunailt', icon: 'steam' },
  ],
  discordInvite: 'https://discord.gg/vGX8GSrej6',
  leadership: [
    {
      name: 'Lytair',
      role: 'Owner & Community Lead',
      tier: 'core',
      scope: 'platform',
      badge: 'Owner',
      avatar: '/avatars/uliunai_avatar_lytair.png',
      initials: 'LY',
      description:
        'Leads the Uliunai.com community. Responsible for community moderation and ensuring a welcoming environment for all players.',
      specialties: ['Community Moderation', 'Player Support'],
    },
    {
      name: 'Greiton',
      role: 'Platform Engineer',
      tier: 'core',
      scope: 'platform',
      badge: 'Core Team',
      avatar: '/avatars/uliunai_avatar_greiton.png',
      initials: 'GR',
      description:
        'Built the Uliunai.com platform from the ground up. Handles the website, admin tools, server infrastructure, and deployment pipeline.',
      specialties: ['Web Development', 'Platform Design', 'Server Infrastructure'],
    },
    {
      name: 'HekuT',
      role: 'Game Developer',
      tier: 'core',
      scope: 'platform',
      badge: 'Core Team',
      avatar: '/avatars/uliunai_avatar_hekut.png',
      initials: 'HT',
      description:
        'The mind behind the gameplay. Develops custom mutators, perks, and weapons, and fine-tunes game balance to keep every match fresh and challenging.',
      specialties: ['Game Balancing', 'Mutator Development', 'Custom Content'],
    },
    {
      name: 'Jackie',
      role: 'Discord Administrator',
      tier: 'admin',
      scope: 'platform',
      badge: 'Admin',
      avatar: '/avatars/uliunai_avatar_jackie.png',
      initials: 'JA',
      description: 'Keeps the Discord running and supports the community day to day.',
    },
    {
      name: 'mez',
      role: 'Administrator',
      tier: 'admin',
      scope: 'platform',
      badge: 'Admin',
      avatar: '/avatars/uliunai_avatar_mez.png',
      initials: 'ME',
      description: 'Part of the admin team.',
    },
    { name: 'habib', role: 'Moderator', tier: 'moderator', scope: 'platform', badge: 'Mod', avatar: '/avatars/uliunai_avatar_habib.png', initials: 'HB' },
    { name: 'Hakk', role: 'Moderator', tier: 'moderator', scope: 'platform', badge: 'Mod', avatar: '/avatars/uliunai_avatar_hakk.png', initials: 'HK' },
    { name: 'Paisely', role: 'Moderator', tier: 'moderator', scope: 'platform', badge: 'Mod', avatar: '/avatars/uliunai_avatar_paisely.png', initials: 'PA' },
    { name: 'Spagh', role: 'Moderator', tier: 'moderator', scope: 'platform', badge: 'Mod', avatar: '/avatars/uliunai_avatar_spagh.png', initials: 'SP' },
  ],
  conduct: [
    { title: 'Be Respectful', summary: 'No insulting, provoking, harassment or discrimination.' },
    { title: 'Fair Play', summary: 'No cheating, exploits, or third-party software.' },
    { title: 'Keep Chat Clean', summary: 'No spam, flooding, impersonation, or advertising.' },
  ],
};

const CommunityContext = createContext<CommunityData>(FALLBACK_COMMUNITY);

/**
 * Access shared community data (social links, Discord invite, leadership,
 * conduct). Always returns a valid object — the inlined fallback until the
 * API resolves.
 */
export function useCommunity(): CommunityData {
  return useContext(CommunityContext);
}

export function CommunityProvider({ children }: { children: React.ReactNode }) {
  const { apiPath } = useLanguage();
  const [data, setData] = useState<CommunityData>(FALLBACK_COMMUNITY);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    async function load() {
      try {
        const res = await fetch(apiPath('/api/community'), { signal: controller.signal });
        if (!res.ok) return;
        const json = (await res.json()) as Partial<CommunityData>;
        if (!active) return;
        setData({
          social: json.social ?? FALLBACK_COMMUNITY.social,
          discordInvite: json.discordInvite ?? FALLBACK_COMMUNITY.discordInvite,
          leadership: json.leadership ?? FALLBACK_COMMUNITY.leadership,
          conduct: json.conduct ?? FALLBACK_COMMUNITY.conduct,
        });
      } catch {
        /* keep fallback */
      }
    }

    void load();
    return () => {
      active = false;
      controller.abort();
    };
  }, [apiPath]);

  return <CommunityContext.Provider value={data}>{children}</CommunityContext.Provider>;
}
